import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./LanguageSwitcher.module.scss";
import UnitedKingdomFlag from "../../icons/united-kingdom.svg";
import NetherlandsFlag from "../../icons/netherlands.svg";
import DownArrowIcon from "../../icons/down-arrow.svg";

const LANGUAGE_COOKIE = "borderly_language";

const getLanguageFromCookie = () => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie?.split(";") || [];
  const match = cookies
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${LANGUAGE_COOKIE}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
};

const setLanguageCookie = (value) => {
  const isLocal = window.location.hostname === "localhost";
  const cookieDomain = isLocal ? "" : "domain=.border.ly;";
  document.cookie = `${LANGUAGE_COOKIE}=${encodeURIComponent(value)}; ${cookieDomain} path=/; secure; samesite=None`;
};

const LanguageSwitcher = ({ onLightBackground = false }) => {
  const { t, i18n } = useTranslation();
  const switcherRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = i18n.language || "en";
  const normalizedLang = currentLang.split("-")[0];

  const languages = useMemo(() => [
    {
      code: "en",
      label: t("languageSwitcher.options.en"),
      shortLabel: t("languageSwitcher.en"),
      flag: UnitedKingdomFlag,
    },
    {
      code: "nl",
      label: t("languageSwitcher.options.nl"),
      shortLabel: t("languageSwitcher.nl"),
      flag: NetherlandsFlag,
    },
  ], [t]);

  const currentLanguage =
    languages.find((lang) => lang.code === normalizedLang) ?? languages[0];

  const handleChangeLanguage = (lng) => {
    if (lng === normalizedLang) return;
    i18n.changeLanguage(lng);
    setLanguageCookie(lng);
    setIsOpen(false);
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        switcherRef.current &&
        !switcherRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const cookieLang = getLanguageFromCookie();
    if (cookieLang && cookieLang !== normalizedLang) {
      i18n.changeLanguage(cookieLang);
      return;
    }
    // ensure cookie exists for current selection
    if (!cookieLang) {
      setLanguageCookie(normalizedLang);
    }
  }, [i18n, normalizedLang]);

  const rootClassName = onLightBackground
    ? `${styles.languageSwitcher} ${styles.onLight}`
    : styles.languageSwitcher;

  const triggerClassName = [
    styles.trigger,
    isOpen ? styles.open : "",
    onLightBackground ? styles.triggerLight : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName} ref={switcherRef}>
      <button
        type="button"
        className={triggerClassName}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.flagWrapper} aria-hidden="true">
          <img src={currentLanguage.flag} alt="" />
        </span>
        <span className={styles.currentValue}>
          {currentLanguage.shortLabel}
        </span>
        <span className={styles.chevron} aria-hidden="true">
          <img src={DownArrowIcon} alt="" />
        </span>
      </button>

      <div
        className={`${styles.dropdown} ${isOpen ? styles.visible : ""}`}
        role="listbox"
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`${styles.option} ${normalizedLang === lang.code ? styles.active : ""
              }`}
            onClick={() => handleChangeLanguage(lang.code)}
            role="option"
            aria-selected={normalizedLang === lang.code}
          >
            <span className={styles.optionFlag}>
              <img src={lang.flag} alt={lang.label} />
            </span>
            <span className={styles.optionCopy}>
              <span className={styles.optionLabel}>{lang.label}</span>
              <span className={styles.optionShort}>{lang.shortLabel}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
